import {
  createProductInitialValues,
  createProductValidationSchema,
} from "@/app/nutrition/create-product.schema";
import {
  useCreateProductNutritionMutation,
  useProductPaginatorQuery,
  useUpdateProductNutritionMutation,
} from "@/app/nutrition/nutrition.api";
import { ProductEditorRecord } from "@/app/nutrition/product";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { CreateProductForm } from "@/shared/components/organisms/CreateProductForm";
import {
  DataTable,
  DataTableColumn,
} from "@/shared/components/organisms/DataTable";
import { useDebounce } from "@/shared/hooks/debounce";
import { toasts } from "@/shared/utils/toasts";
import { create } from "@amcharts/amcharts4/core";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { useFormik } from "formik";
import { PlusIcon } from "lucide-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
} from "react";
import { useTranslation } from "react-i18next";

export type ProductsPageProps = {};

export const ProductsPage: FC<ProductsPageProps> = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState();
  const [createProductVisible, setCreateProductVisible] = useState(false);

  const [updateProductNutrition, updateProductNutritionResult] =
    useUpdateProductNutritionMutation();
  const [createProductNutrition, createProductNutritionResult] =
    useCreateProductNutritionMutation();

  const searchFormik = useFormik({
    initialValues: {
      search: "",
      page: 1,
    },
    onSubmit: (values) => {},
  });

  const debouncedSearch = useDebounce(searchFormik.values, 300);

  const productPaginatorQuery = useProductPaginatorQuery({
    page: debouncedSearch.page,
    product: debouncedSearch.search,
  });

  const formik = useFormik({
    initialValues: createProductInitialValues,
    validationSchema: createProductValidationSchema,
    validateOnBlur: true,
    onSubmit: (values) => {
      createProductNutrition({
        product: values.product,
        equivalences: values.equivalences,
        nutritionalInformation: values.nutritionalInformation,
        carts: values.carts,
      });
    },
  });

  useEffect(() => {
    if (createProductNutritionResult.isSuccess) {
      toasts.success(
        t("contexts.nutrition.create_product_success"),
        t("contexts.nutrition.create_product_success_description")
      );
    }

    if (createProductNutritionResult.isError) {
      toasts.danger(
        t("contexts.nutrition.create_product_error"),
        t("contexts.nutrition.create_product_error_description")
      );
    }
  }, [createProductNutritionResult]);

  const columns = useMemo<DataTableColumn<ProductEditorRecord>[]>(() => {
    return [
      {
        field: "product",
        label: "Español",
      },
      {
        field: "productEn",
        label: "Inglés",
      },
      {
        field: "productPt",
        label: "Portugués",
      },
      {
        field: "calories",
        label: t("contexts.nutrition.calories"),
        mapper: (value) => `${value} kcal`,
      },
      {
        field: "proteins",
        label: t("contexts.nutrition.proteins"),
        mapper: (value) => `${value} gr`,
      },
      {
        field: "carbohydrates",
        label: t("contexts.nutrition.carbohydrates"),
        mapper: (value) => `${value} gr`,
      },
      {
        field: "fats",
        label: t("contexts.nutrition.fats"),
        mapper: (value) => `${value} gr`,
      },
      {
        field: "fibers",
        label: t("contexts.nutrition.fibers"),
        mapper: (value) => `${value} gr`,
      },
    ];
  }, [productPaginatorQuery.data]);

  const handleEquivalence = useCallback(() => {
    (async () => {
      await formik.validateForm();
      const hasErrors =
        formik.errors.temp?.equivalences?.quantity ||
        formik.errors.temp?.equivalences?.portionEn ||
        formik.errors.temp?.equivalences?.portionEs ||
        formik.errors.temp?.equivalences?.portionPt;

      const hasEmptyValues =
        !formik.values.temp.equivalences.quantity ||
        !formik.values.temp.equivalences.portionEn ||
        !formik.values.temp.equivalences.portionEs ||
        !formik.values.temp.equivalences.portionPt;

      if (hasErrors || hasEmptyValues) {
        toasts.danger(
          t("contexts.shared.invalid_action"),
          t("contexts.nutrition.equivalences_invalid")
        );
        return;
      }

      formik.setFieldValue(
        `equivalences[${formik.values.equivalences?.length}]`,
        formik.values.temp.equivalences
      );
      formik.setFieldValue("temp.equivalences", {
        quantity: "",
        portionEs: "",
        portionEn: "",
        portionPt: "",
      });
    })();
  }, [formik.values]);

  const handleEquivalenceDelete = useCallback(
    (index: number) => {
      formik.setFieldValue(`equivalences`, [
        ...formik.values.equivalences.slice(0, index),
        ...formik.values.equivalences.slice(index + 1),
      ]);
    },
    [formik.values]
  );

  const handleCart = useCallback(() => {
    (async () => {
      await formik.validateForm();
      const hasErrors =
        formik.errors.temp?.carts?.en ||
        formik.errors.temp?.carts?.es ||
        formik.errors.temp?.carts?.pt;

      const hasEmptyValues =
        !formik.values.temp.carts.en ||
        !formik.values.temp.carts.es ||
        !formik.values.temp.carts.pt;

      if (hasErrors || hasEmptyValues) {
        toasts.danger(
          t("contexts.shared.invalid_action"),
          t("contexts.nutrition.carts_invalid")
        );
        return;
      }

      formik.setFieldValue(
        `carts[${formik.values.carts?.length}]`,
        formik.values.temp.carts
      );
      formik.setFieldValue("temp.carts", {
        en: "",
        es: "",
        pt: "",
      });
    })();
  }, [formik.values]);

  const handleCartDelete = useCallback(
    (index: number) => {
      formik.setFieldValue(`carts`, [
        ...formik.values.carts.slice(0, index),
        ...formik.values.carts.slice(index + 1),
      ]);
    },
    [formik.values]
  );

  console.debug(formik.errors);

  const handleEdit = useCallback((item: ProductEditorRecord, index: number) => {
    console.debug(item);
  }, []);

  useEffect(() => {
    if (createProductNutritionResult.isSuccess) {
      formik.resetForm();
      createProductNutritionResult.reset();
    }
  }, [createProductNutritionResult]);

  return (
    <Fragment>
      <div className="flex flex-col flex-1 h-full w-full overflow-y-scroll pt-8">
        <div className="flex flex-1 flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
          <div className="flex-1 flex-col px-4 sm:px-6 lg:px-6">
            <div className="sm:flex sm:items-center">
              <div className="flex-grow">
                <h1 className="text-base font-semibold leading-6 text-gray-900">
                  {t("contexts.nutrition.products")}
                </h1>
                <p className="mt-2 text-sm text-gray-700">
                  {t("contexts.nutrition.manage_products")}
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0"></div>
            </div>
            <div className="border-b border-gray-300 my-3"></div>
            <div className="sm:flex sm:items-center">
              <div className="sm:flex w-64">
                <InputWithCornerHint
                  label={t("general.search")}
                  name="search"
                  type="text"
                  placeholder={t("contexts.nutrition.product_name")}
                  error={false}
                  value={searchFormik.values.search}
                  onChange={searchFormik.handleChange}
                  onBlur={searchFormik.handleBlur}
                />
              </div>
              <div className="mt-8 sm:ml-auto sm:mt-4 sm:flex sm:items-center sm:space-x-4 ">
                <button
                  onClick={() => {}}
                  type="button"
                  className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
                >
                  <PlusIcon
                    aria-hidden="true"
                    className="h-5 w-5"
                    onClick={() => setCreateProductVisible(true)}
                  />
                </button>
              </div>
            </div>
            <div className="border-b border-gray-300 mt-5"></div>
            <div className="flex flex-col w-full">
              <DataTable<ProductEditorRecord>
                columns={columns}
                isLoading={
                  productPaginatorQuery.isLoading ||
                  productPaginatorQuery.isFetching
                }
                data={productPaginatorQuery.data?.data ?? []}
                currentPage={page}
                onPageChange={(page) => setPage(() => page)}
                pageSize={productPaginatorQuery.data?.perPage}
                totalResults={productPaginatorQuery.data?.total}
                totalPages={productPaginatorQuery.data?.lastPage}
                actions={[
                  {
                    icon: faPencil,
                    label: "Editar",
                    handler: handleEdit,
                  },
                ]}
              ></DataTable>
            </div>
          </div>
        </div>
      </div>
      <DialogCreate
        isLoading={createProductNutritionResult.isLoading}
        tittleButtonCancel={t("general.cancel")}
        tittleButtonCreate="Crear"
        open={createProductVisible}
        OnclickCancel={() => setCreateProductVisible(!createProductVisible)}
        OnclickCreate={() => {
          formik.handleSubmit();
        }}
        acceptDisabled={!formik.isValid}
        setOpen={() => setCreateProductVisible(!createProductVisible)}
        tittle={t("contexts.nutrition.wizard_title")}
        subTittle={t("contexts.nutrition.wizard_subtitle")}
      >
        <CreateProductForm
          errors={formik.errors}
          values={formik.values}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          onEquivalence={handleEquivalence}
          onEquivalenceDelete={handleEquivalenceDelete}
          onCart={handleCart}
          onCartDelete={handleCartDelete}
          isLoading={createProductNutritionResult.isLoading}
        />
      </DialogCreate>
    </Fragment>
  );
};
